import { camelcaseKeysFromApi, snakecaseKeysForApi } from '@vestigas/data';

/**
 * `docuware_field_mapping` is keyed by the customer's DocuWare index field
 * names ("MANDANT", "FIRMA_LIEFERANT"), which DocuWare matches case
 * sensitively. The API client's automatic case conversion would rewrite them to
 * "mandant" and "firmaLieferant", so every index field would be dropped on
 * upload. Contact points therefore travel on the conversion-free client, with
 * these fields carried across verbatim and everything else converted here.
 */
const USER_KEYED_FIELDS = [
  // Header names, X-Api-Key would arrive as x_api_key
  ['api_headers', 'apiHeaders'],
  ['docuware_field_mapping', 'docuwareFieldMapping'],
] as const;

const UI_TO_API_FIELDS = USER_KEYED_FIELDS.map(
  ([apiName, uiName]) => [uiName, apiName] as const,
);

/** A null is kept since it clears the field, an absent field stays absent */
function splitUserKeyedFields(
  record: Record<string, unknown>,
  fields: ReadonlyArray<readonly [string, string]>,
) {
  const convertible = { ...record };
  const preserved: Record<string, unknown> = {};

  for (const [from, to] of fields) {
    if (convertible[from] !== undefined) {
      preserved[to] = convertible[from];
    }
    delete convertible[from];
  }

  return { convertible, preserved };
}

/** API response -> UI shape, keeping the user-typed keys intact */
export function contactPointFromApi(response: Record<string, unknown>) {
  const { convertible, preserved } = splitUserKeyedFields(
    response,
    USER_KEYED_FIELDS,
  );

  return {
    ...camelcaseKeysFromApi(convertible),
    ...preserved,
  } as Record<string, unknown>;
}

/** UI shape -> API body, keeping the user-typed keys intact */
export function contactPointToApi(data: Record<string, unknown>) {
  const { convertible, preserved } = splitUserKeyedFields(
    data,
    UI_TO_API_FIELDS,
  );

  return {
    ...(snakecaseKeysForApi(convertible) as Record<string, unknown>),
    ...preserved,
  };
}
